import React, { useState } from 'react';
import api from '../api/api';
import Icon from '../components/Icon';
import { EditableList, EditableText } from '../components/Editable';
import LogoPicker from '../product/LogoPicker';
import { useProducts } from '../product/ProductContext';
import {
    PRODUCT_FIELDS, formatFieldValue, formatRating, formatShortDate, isEmptyValue, sourceIcon, sourceName,
} from '../product/productUtils';

const describeSources = (sources = []) => {
    const named = sources.filter(s => s.type === 'landing' || s.type === 'store').map(sourceName);
    const uploads = sources.filter(s => s.type === 'upload' || s.type === 'text').length;
    const parts = [...named.slice(0, 2), uploads ? `${uploads} upload${uploads === 1 ? '' : 's'}` : null].filter(Boolean);
    if (parts.length <= 1) return parts[0] || '';
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

// Shows what a re-sync would change and applies only what the user ticks
const ResyncDiff = ({ product, result, onApply, onClose }) => {
    const changes = PRODUCT_FIELDS
        .map(field => {
            const next = result.fields?.[field.id];
            if (!next || isEmptyValue(next.value)) return null;
            const before = formatFieldValue(field.id, product[field.id]);
            const after = formatFieldValue(field.id, next.value);
            return before === after ? null : { field, before, after, value: next.value, source: next.source };
        })
        .filter(Boolean);
    const [selected, setSelected] = useState(() => changes.map(change => change.field.id));
    const [isSaving, setIsSaving] = useState(false);

    const toggle = (id) => setSelected(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));

    const apply = async () => {
        setIsSaving(true);
        const picked = changes.filter(change => selected.includes(change.field.id));
        await onApply(
            picked.reduce((acc, change) => ({ ...acc, [change.field.id]: change.value }), {}),
            picked.reduce((acc, change) => ({ ...acc, [change.field.id]: { source: change.source, confidence: 'high' } }), {}),
        );
        setIsSaving(false);
    };

    return (
        <div className="ls-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="ls-modal" role="dialog" aria-modal="true" aria-labelledby="resync-title">
                <div>
                    <div id="resync-title" style={{ fontSize: 19, fontWeight: 700 }}>Re-sync results</div>
                    <div style={{ fontSize: 14, color: 'var(--secondary)', marginTop: 4 }}>
                        {changes.length
                            ? 'Your sources say something different for these fields. Pick the changes to keep.'
                            : 'Your profile already matches your sources. Nothing to change.'}
                    </div>
                </div>
                {changes.map(change => (
                    <label className="ls-diff-row" key={change.field.id}>
                        <input
                            type="checkbox"
                            checked={selected.includes(change.field.id)}
                            onChange={() => toggle(change.field.id)}
                            style={{ accentColor: 'var(--accent-strong)', marginTop: 3 }}
                        />
                        <span style={{ color: 'var(--muted)' }}>{change.field.label}</span>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {change.before && <span className="ls-diff-old">{change.before}</span>}
                            <span>{change.after}</span>
                            {change.source && <span style={{ fontSize: 12, color: 'var(--muted)' }}>From {change.source}</span>}
                        </span>
                    </label>
                ))}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={onClose}>{changes.length ? 'Cancel' : 'Close'}</button>
                    {changes.length > 0 && (
                        <button type="button" className="ls-btn ls-btn-primary" onClick={apply} disabled={isSaving || selected.length === 0}>
                            {isSaving ? 'Applying...' : `Apply ${selected.length} change${selected.length === 1 ? '' : 's'}`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const AddSource = ({ onAdd }) => {
    const [type, setType] = useState('landing');
    const [url, setUrl] = useState('');
    const [isReading, setIsReading] = useState(false);
    const [error, setError] = useState('');

    const submit = async (event) => {
        event.preventDefault();
        if (!url.trim()) return;
        setIsReading(true);
        setError('');
        try {
            const read = await api.readSource(type, url.trim());
            await onAdd({
                id: `src-${Date.now()}`,
                type,
                title: read.title || url.trim(),
                detail: read.detail || '',
                url: read.url || url.trim(),
                ...(read.icon && { icon: read.icon }),
                addedAt: new Date().toISOString(),
            });
            setUrl('');
        } catch (e) {
            setError(e.response?.data?.error || "Couldn't read that page.");
        } finally {
            setIsReading(false);
        }
    };

    return (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 6 }}>
                <select className="ls-select is-compact" style={{ width: 'auto' }} value={type} onChange={(e) => setType(e.target.value)} aria-label="Source type">
                    <option value="landing">Landing page</option>
                    <option value="store">App Store / Play</option>
                </select>
                <input className="ls-input is-compact" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" aria-label="Source URL" autoFocus />
                <button type="submit" className="ls-btn ls-btn-pill" disabled={isReading}>{isReading ? 'Reading…' : 'Add'}</button>
            </div>
            {error && <span style={{ fontSize: 12, color: 'var(--danger-text)' }}>{error}</span>}
        </form>
    );
};

const ProductProfileScreen = () => {
    const { currentProduct: product, updateProduct } = useProducts();
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [isSyncing, setIsSyncing] = useState(false);
    const [resync, setResync] = useState(null);
    const [addingSource, setAddingSource] = useState(false);

    const save = async (changes) => {
        setError('');
        try {
            await updateProduct(product.id, { ...changes, setupDone: { profile: true } });
        } catch (e) {
            setError("Couldn't save your change. Please try again.");
        }
    };

    const handleResync = async () => {
        setIsSyncing(true);
        setError('');
        try {
            setResync(await api.resyncProduct(product.id));
        } catch (e) {
            setError(e.response?.data?.error || "Couldn't re-read your sources. Please try again.");
        } finally {
            setIsSyncing(false);
        }
    };

    const applyResync = async (changes, fieldSources) => {
        await save({
            ...changes,
            fieldSources: { ...product.fieldSources, ...fieldSources },
            syncedAt: new Date().toISOString(),
        });
        setResync(null);
        setNotice(Object.keys(changes).length ? 'Profile updated from your sources.' : '');
    };

    const addSource = async (source) => {
        await save({ sources: [...(product.sources || []), source] });
        setAddingSource(false);
        setNotice(`Added ${source.title}. Re-sync to pull facts from it.`);
    };

    const removeSource = (source) => {
        if (!window.confirm(`Remove ${source.title} from your sources? Facts already in your profile stay.`)) return;
        save({ sources: product.sources.filter(s => s.id !== source.id) });
    };

    const features = product.features || [];
    const proof = product.proof || { rating: null, ratingCount: null, quotes: [] };
    const sources = product.sources || [];
    const meta = [product.category, product.platforms?.join(', '), formatRating(proof)].filter(Boolean).join(' · ');
    const syncedFrom = describeSources(sources);
    const quoteSource = sources.find(s => s.type === 'store');

    const updateFeature = (index, changes) => save({
        features: features.map((feature, i) => (i === index ? { ...feature, ...changes } : feature)).filter(feature => feature.name),
    });

    return (
        <>
            <div className="ls-page-header">
                <div>
                    <h1 className="ls-page-title">Product profile</h1>
                    <div className="ls-eyebrow" style={{ marginTop: 2 }}>
                        {product.syncedAt
                            ? `Last synced ${formatShortDate(product.syncedAt)}${syncedFrom ? ` from ${syncedFrom}` : ''}`
                            : 'These facts go into every post, plan and ad the AI writes. Click any value to edit it.'}
                    </div>
                </div>
                <button
                    type="button"
                    className="ls-btn ls-btn-secondary"
                    onClick={handleResync}
                    disabled={isSyncing || !sources.some(s => s.type !== 'quiz')}
                    title={sources.some(s => s.type !== 'quiz') ? 'Re-read your sources and review the changes' : 'Add a source first'}
                >
                    {isSyncing ? <span className="ls-spinner" /> : <Icon name="sync" size={18} />}
                    {isSyncing ? 'Reading sources…' : 'Re-sync sources'}
                </button>
            </div>

            {error && <div className="ls-error" role="alert"><Icon name="error" />{error}</div>}
            {notice && <div className="ls-notice">{notice}</div>}

            <div className="ls-two-col">
                <div className="ls-col">
                    <div className="ls-card" style={{ padding: 22, gap: 14 }}>
                        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                            <LogoPicker product={product} onSave={(logo) => updateProduct(product.id, { logo })} />
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 19, fontWeight: 700 }}>
                                    <EditableText value={product.name} label="Name" onSave={(name) => name && save({ name })} />
                                </div>
                                {meta && <div style={{ fontSize: 13, color: 'var(--muted)' }}>{meta}</div>}
                            </div>
                        </div>
                        <div style={{ fontSize: 15, lineHeight: 1.55 }}>
                            <EditableText
                                value={product.oneLiner}
                                multiline
                                label="One-liner"
                                placeholder="Add a one-line pitch"
                                onSave={(oneLiner) => save({ oneLiner })}
                            />
                        </div>
                        <div className="ls-kv">
                            <span>Pricing</span>
                            <EditableText value={product.pricing} label="Pricing" onSave={(pricing) => save({ pricing })} />
                            <span>Current offer</span>
                            <EditableText value={product.offer} label="Current offer" placeholder="None right now" onSave={(offer) => save({ offer })} />
                            <span>Content goal</span>
                            <EditableText value={product.goal} label="Content goal" onSave={(goal) => save({ goal })} />
                            <span>Target customer</span>
                            <EditableText value={product.targetCustomer} label="Target customer" onSave={(targetCustomer) => save({ targetCustomer })} />
                            <span>Category</span>
                            <EditableText value={product.category} label="Category" onSave={(category) => save({ category })} />
                            <span>Platforms</span>
                            <EditableText
                                value={(product.platforms || []).join(', ')}
                                label="Platforms"
                                placeholder="e.g. iOS, Android, Web"
                                onSave={(text) => save({ platforms: text.split(',').map(p => p.trim()).filter(Boolean) })}
                            />
                        </div>
                    </div>

                    <div className="ls-card" style={{ padding: 22 }}>
                        <div className="ls-card-title">Features</div>
                        {features.length === 0 && <div className="ls-empty">List what the product does, so posts can show it off.</div>}
                        <div className="ls-feature-grid">
                            {features.map((feature, index) => (
                                <div className="ls-feature" key={`${feature.name}-${index}`}>
                                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                                        <EditableText value={feature.name} label="Feature name" onSave={(name) => updateFeature(index, { name })} />
                                    </div>
                                    <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
                                        <EditableText value={feature.detail} label="Feature detail" placeholder="Add a detail" onSave={(detail) => updateFeature(index, { detail })} />
                                    </div>
                                    <button
                                        type="button"
                                        className="ls-chip-remove"
                                        aria-label={`Remove ${feature.name}`}
                                        onClick={() => save({ features: features.filter((_, i) => i !== index) })}
                                    >
                                        <Icon name="close" size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <EditableList
                            items={[]}
                            as="lines"
                            placeholder="Add feature"
                            label="features"
                            onSave={(added) => save({ features: [...features, ...added.map(name => ({ name, detail: '' }))] })}
                        />
                    </div>
                </div>

                <div className="ls-col">
                    <div className="ls-card" style={{ padding: 22, gap: 10 }}>
                        <div className="ls-card-title">What customers say</div>
                        {proof.rating != null && (
                            <div style={{ fontSize: 14, fontWeight: 600 }}>
                                {formatRating(proof)}{proof.ratingCount ? ' ratings' : ''}
                            </div>
                        )}
                        <div style={{ color: 'var(--body)' }}>
                            <EditableList
                                items={proof.quotes || []}
                                as="lines"
                                quote
                                placeholder="Add a quote"
                                label="customer quotes"
                                onSave={(quotes) => save({ proof: { ...proof, quotes } })}
                            />
                        </div>
                        {quoteSource && proof.quotes?.length > 0 && <div style={{ fontSize: 12, color: 'var(--muted)' }}>From {quoteSource.title} reviews</div>}
                    </div>

                    <div className="ls-card" style={{ padding: 22, gap: 10 }}>
                        <div className="ls-card-title">Competitors</div>
                        <EditableList
                            items={product.competitors || []}
                            placeholder="Competitor name"
                            label="competitors"
                            onSave={(competitors) => save({ competitors })}
                        />
                    </div>

                    <div className="ls-card" style={{ padding: 22, gap: 10 }}>
                        <div className="ls-card-header">
                            <span className="ls-card-title">Sources</span>
                            <button type="button" className="ls-link" onClick={() => setAddingSource(!addingSource)}>
                                {addingSource ? 'Cancel' : '+ Add'}
                            </button>
                        </div>
                        {addingSource && <AddSource onAdd={addSource} />}
                        {sources.length === 0 && !addingSource && <div className="ls-empty">No sources yet. Add your landing page or app store listing.</div>}
                        {sources.map(source => (
                            <div className="ls-list-line" key={source.id} style={{ alignItems: 'center', gap: 10, fontSize: 14 }}>
                                <Icon name={sourceIcon(source)} size={18} color="var(--secondary)" />
                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={source.url || source.detail}>
                                    {source.title}
                                </span>
                                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{formatShortDate(source.addedAt)}</span>
                                {source.type !== 'quiz' && (
                                    <button type="button" className="ls-chip-remove" style={{ marginTop: 0 }} aria-label={`Remove ${source.title}`} onClick={() => removeSource(source)}>
                                        <Icon name="close" size={16} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {resync && (
                <ResyncDiff product={product} result={resync} onApply={applyResync} onClose={() => setResync(null)} />
            )}
        </>
    );
};

export default ProductProfileScreen;
